import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight, Globe2 } from "lucide-react";
import Reveal from "./Reveal";
import { BACKEND } from "./data";
import { useT } from "./i18n";

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function eligibility(job) {
  const out = [];
  (job.criteria_mandatory || []).forEach((c) => {
    if (c.type === "min_experience") out.push(`${c.value}+ yrs`);
    else if (c.type === "skill") out.push(c.value);
    else if (c.type === "language") out.push(`${c.value}`);
    else if (c.type === "profession") out.push(cap(c.value));
  });
  return out.slice(0, 4);
}

export default function JobsPreview() {
  const { t } = useT();
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/jobs`)
      .then((r) => (r.ok ? r.json() : { jobs: [] }))
      .then((d) => { if (!cancelled) setJobs((d.jobs || []).slice(0, 6)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="jobs" className="scroll-mt-24 bg-[#FDFBF7] py-20">
      <div className="mx-auto max-w-6xl px-5">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C4A46B]">{t.jobs.eyebrow}</p>
            <h2 className="mt-3 max-w-xl font-serif text-4xl leading-tight text-[#173A2C] sm:text-5xl">{t.jobs.heading}</h2>
          </div>
          <Link to="/register" data-testid="jobs-join-btn" className="inline-flex items-center gap-1 rounded-full bg-[#1F4A38] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
            {t.jobs.join} <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>

        {jobs.length === 0 ? (
          <Reveal className="mt-10 rounded-2xl border border-dashed border-[#E8E4DB] bg-white p-12 text-center">
            <Globe2 className="mx-auto h-8 w-8 text-[#C4A46B]" />
            <p className="mt-3 text-sm text-[#5C5855]">{t.jobs.empty}</p>
          </Reveal>
        ) : (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((j, i) => (
              <Reveal key={j.id} delay={(i % 3) + 1} className="flex h-full flex-col rounded-2xl border border-[#E8E4DB] bg-white p-6 card-lift">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[#1F4A38]/8 px-2.5 py-0.5 text-[11px] font-semibold text-[#1F4A38]">{cap(j.profession)}</span>
                  <span className="rounded-full border border-[#E8E4DB] px-2.5 py-0.5 text-[11px] text-[#5C5855]">{j.type || t.jobs.fulltime}</span>
                </div>
                <h3 className="mt-3 font-serif text-xl text-[#173A2C]">{j.title}</h3>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5C5855]">
                  <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {j.employer_org || t.jobs.verifiedEmployer}</span>
                  {j.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {j.location}</span>}
                </div>
                {eligibility(j).length > 0 && (
                  <div className="mt-4 border-t border-[#F0ECE3] pt-3">
                    <p className="text-[10px] uppercase tracking-wide text-[#8a8580]">{t.jobs.eligibility}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {eligibility(j).map((e, k) => (
                        <span key={k} className="rounded-full bg-[#C4A46B]/12 px-2.5 py-0.5 text-xs text-[#8a6f38]">{e}</span>
                      ))}
                    </div>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
